import apiClient from '@/utils/request'
import { nestedQueryFormatter } from '@/utils/query'
import { WEEKDAYS_COUNT } from '@/constants'

import {
  IRequestPayload,
  ISpecificVersion,
  IGeneralResponse,
  RelationshipEntry,
  ILimit,
  DspAdType,
  RonType,
  Stream,
  Device,
  ConnectionType,
  Platform,
  SpecificVersion,
  Browser,
  Weekday,
  Timeslot,
  DeviceLanguage
} from '@/api/types'
import { ICreative } from '@/apps/traforama/interfaces/creatives'
import {
  ICampaignAttributes,
  ICampaignData,
  ICampaignsPayload,
  ICampaignsResponse,
  ICampaignResponse
} from '@/apps/traforama/interfaces/campaigns'

const CampaignToRequest = (campaign: ICampaignAttributes): ICampaignData => {
  const campaignData: ICampaignData = {
    name: campaign.name,
    stream: campaign.stream,
    type_id: campaign.type_id,
    included_spots: [],
    excluded_spots: [],
    country_codes: campaign.country_codes,
    city_ids: campaign.city_ids,
    region_ids: campaign.region_ids,
    isp: campaign.isp,
    device_ids: campaign.device_ids,
    connection_type_ids: campaign.connection_type_ids,
    // API expects numbers in platform_ids, but strings in specific platforms
    // It's easier just to convert to int before sending API request
    platform_ids: campaign.platform_ids.map((platform) =>
      parseInt(platform as string)
    ),
    browser_ids: campaign.browser_ids.map((browser) =>
      parseInt(browser as string)
    ),
    specific_platforms: campaign.specific_platforms,
    specific_browsers: campaign.specific_browsers,
    limits: campaign.limits,
    weekdays:
      campaign.weekdays.length === WEEKDAYS_COUNT ? [] : campaign.weekdays,
    hours: campaign.hours,
    size_id: campaign.size_id,
    whitelist_domains: campaign.whitelist_domains,
    blacklist_domains: campaign.blacklist_domains,
    use_whitelist_domains: campaign.use_whitelist_domains,
    whitelist_user_agents: campaign.whitelist_user_agents,
    blacklist_user_agents: campaign.blacklist_user_agents,
    use_whitelist_user_agents: campaign.use_whitelist_user_agents,
    language_ids: campaign.language_ids,
    ip_range_type: campaign.use_whitelist_ip ? 1 : 0,
    keywords: campaign.keywords,
    spots_list_ids: campaign.spots_list_ids,
    ip_list_ids: campaign.ip_list_ids,
    ips: campaign.ips,
    ron_type: campaign.ron_type,
    limits_slow_stop: campaign.limits_slow_stop,
    limits_rps: campaign.limits_rps,
    slow_stop_factor: campaign.slow_stop_factor,
    rps_factor: campaign.rps_factor,
    isp_mode: campaign.isp_mode,
    retargeting_list_ids: campaign.retargeting_list_ids
  }

  if (campaign.start_at !== '') {
    campaignData.start_at = campaign.start_at
  }

  if (campaign.finish_at !== '') {
    campaignData.finish_at = campaign.finish_at
  }

  switch (campaign.ron_type) {
    case RonType.Only:
      campaignData.included_spots = campaign.spot_ids
      break
    case RonType.Except:
      campaignData.excluded_spots = campaign.spot_ids
  }

  return campaignData
}

export const fetchItems = async (
  payload: ICampaignsPayload
): Promise<ICampaignsResponse> => {
  const params = Object.assign(
    { limit: payload.limit, start: payload.start },
    nestedQueryFormatter('filter', payload.filter, false)
    // nestedQueryFormatter('sorter', payload.sorter)
  )
  const response = await apiClient.get<ICampaignsResponse>('/campaigns', {
    params: params
  })
  return response.data
}

export const createItem = async (
  campaign: ICampaignAttributes
): Promise<ICampaignResponse> => {
  const response = await apiClient.post<ICampaignResponse>(
    '/campaigns',
    CampaignToRequest(campaign)
  )
  return response.data
}

export const updateItem = async (campaign: ICampaignAttributes) => {
  const response = await apiClient.put<ICampaignResponse>(
    `/campaigns/${campaign.id}`,
    CampaignToRequest(campaign)
  )
  return response.data
}

export const fetchItem = async (id: string): Promise<ICampaignResponse> => {
  const response = await apiClient.get<ICampaignResponse>(`/campaigns/${id}`)
  return response.data
}

export const destroyItem = async (id: string) => {
  await apiClient.delete(`/campaigns/${id}`)
}

export const archiveItem = async (id: string) => {
  await apiClient.post(`/campaigns/${id}/archive`)
}
